import React, { useCallback, useEffect, useMemo, useState } from "react";
import useUser from "../../../hooks/useUser";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Buttons/Button/Button";
import ButtonDelete from "../../components/Buttons/ButtonDelete/ButtonDelete";
import {
  getOrganizationSubscription,
  getSubscriptionProductsDropdown,
  cancelOrganizationSubscription,
  createSubscriptionCheckout,
} from "../../../services/subscriptionsService";
import ReviewChangesModal from "./ReviewChangesModal/ReviewChangesModal";
import CancelSubscriptionModal from "./CancelSubscriptionModal/CancelSubscriptionModal";
import CreditPackagesModal from "./CreditPackagesModal/CreditPackagesModal";
import SubscriptionPlansSection from "./SubscriptionPlansSection/SubscriptionPlansSection";
import {
  normalizeSubscriptionProducts,
  isSubscriptionCancelled,
  getPriceForBilling,
  getAmountWithBilling,
  getSubscriptionActivePriceId,
  productHasPriceId,
} from "./subscriptionUtils";
import useFormatDate from "../../../hooks/useFormatDate";
import "./Suscriptions.css";

function Suscriptions() {
  const { user } = useUser();
  const { t } = useTranslation("suscriptions");
  const navigate = useNavigate();
  const formatDate = useFormatDate();

  const organizationId = useMemo(() => {
    const org = user?.organization || user?.organization_id;
    if (!org) return null;
    if (typeof org === "object") {
      return org.id || org._id || null;
    }
    return org;
  }, [user?.organization, user?.organization_id]);

  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [products, setProducts] = useState([]);

  // Campos simples para probar los endpoints desde la UI
  const [cancelReason] = useState("");

  // Estado de UI: plan seleccionado y ciclo de facturación por producto
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [billingByProduct, setBillingByProduct] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCreditPackagesModal, setShowCreditPackagesModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutCompleted, setCheckoutCompleted] = useState(false);
  const [activePriceOverride, setActivePriceOverride] = useState(null);

  const handleChangeBilling = (productId, value) => {
    setBillingByProduct((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleSelectProduct = (productId) => {
    setSelectedProductId(productId);
  };

  const hasActiveSubscription = !!subscription;
  const currentProduct = (() => {
    if (!hasActiveSubscription || !Array.isArray(products) || products.length === 0) {
      return null;
    }

    const activePriceId =
      activePriceOverride || getSubscriptionActivePriceId(subscription);

    // 0) Intentamos matchear por el price_id activo (más confiable que el nombre)
    if (activePriceId) {
      const byPrice = products.find((p) => productHasPriceId(p, activePriceId));
      if (byPrice) return byPrice;
    }

    const subName = subscription?.subscription_name || subscription?.name;
    const subProductId =
      subscription?.stripe_product_id ||
      subscription?.product_stripe_id ||
      subscription?.stripe_product ||
      subscription?.product_id ||
      subscription?.product?.stripe_product_id ||
      null;

    // 1) Intentamos matchear por stripe_product_id / product_id
    let match = products.find((p) => {
      const pid =
        p.stripe_product_id ||
        p.product_stripe_id ||
        p.stripe_product ||
        p.product_id ||
        null;
      return subProductId && pid && pid === subProductId;
    });

    // 2) Si no hay match por ID, intentamos por nombre de suscripción
    if (!match && subName) {
      match = products.find((p) => {
        const name = p.subscription_name || p.name;
        return name === subName;
      });
    }

    // 3) Fallback: primer producto
    return match || products[0] || null;
  })();


  const currentTotal = (() => {
    if (!currentProduct || !hasActiveSubscription) return 0;
    const normalizeBilling = (value) =>
      value === "year" || value === "yearly" ? "yearly" : "monthly";
    const plansValue =
      subscription?.plans || subscription?.subscription?.plans || null;
    let activePlan = null;
    if (Array.isArray(plansValue)) {
      activePlan =
        plansValue.find((p) => p?.active === true) || plansValue[0] || null;
    } else if (plansValue && typeof plansValue === "object") {
      activePlan = plansValue;
    }
    const originalBilling = normalizeBilling(
      activePlan?.billing_cycle ||
        subscription?.current_plan?.billing_cycle ||
        subscription?.billing_cycle ||
        subscription?.plan?.billing_cycle ||
        currentProduct.billing_cycle ||
        currentProduct.cycle ||
        "monthly"
    );
    // Current total should reflect the active subscription as it is now,
    // without being affected by UI changes in the dropdown.
    return getAmountWithBilling(currentProduct, originalBilling);
  })();

  const selectedProduct =
    (products || []).find((p, index) => {
      const id = p.id ?? index;
      return id === selectedProductId;
    }) || currentProduct;

  const newTotal = (() => {
    if (!selectedProduct) return 0;
    const index = (products || []).findIndex((p, idx) => {
      const id = p.id ?? idx;
      return id === selectedProductId;
    });
    const fallbackIndex = index < 0 ? 0 : index;
    const rawId = selectedProduct.id ?? fallbackIndex;
    // Si el producto seleccionado es el actual, usamos el billing real de la
    // suscripción (por ejemplo yearly) en lugar de asumir monthly por defecto.
    let defaultBilling;
    if (hasActiveSubscription && currentProduct && selectedProduct === currentProduct) {
      const normalizeBilling = (value) =>
        value === "year" || value === "yearly" ? "yearly" : "monthly";
      const plansValue =
        subscription?.plans || subscription?.subscription?.plans || null;
      let activePlan = null;
      if (Array.isArray(plansValue)) {
        activePlan =
          plansValue.find((p) => p?.active === true) || plansValue[0] || null;
      } else if (plansValue && typeof plansValue === "object") {
        activePlan = plansValue;
      }
      defaultBilling = normalizeBilling(
        activePlan?.billing_cycle ||
          subscription?.current_plan?.billing_cycle ||
          subscription?.billing_cycle ||
          subscription?.plan?.billing_cycle ||
          currentProduct.billing_cycle ||
          currentProduct.cycle ||
          "monthly"
      );
    } else {
      defaultBilling =
        selectedProduct.billing_cycle || selectedProduct.cycle || "monthly";
    }
    const billing = billingByProduct[rawId] ?? defaultBilling;
    return getAmountWithBilling(selectedProduct, billing);
  })();

  const hasChanges = (() => {
    if (!selectedProduct) return false;
    if (!hasActiveSubscription) return true;

    const normalizeBilling = (value) =>
      value === "year" || value === "yearly" ? "yearly" : "monthly";

    const currentIndex = (products || []).findIndex((p, idx) => {
      const id = p.id ?? idx;
      const currentId = currentProduct.id ?? 0;
      return id === currentId;
    });
    const currentId = currentProduct.id ?? (currentIndex < 0 ? 0 : currentIndex);
    const plansValue =
      subscription?.plans || subscription?.subscription?.plans || null;
    let activePlan = null;
    if (Array.isArray(plansValue)) {
      activePlan =
        plansValue.find((p) => p?.active === true) || plansValue[0] || null;
    } else if (plansValue && typeof plansValue === "object") {
      activePlan = plansValue;
    }
    const currentOriginalBilling = normalizeBilling(
      activePlan?.billing_cycle ||
        subscription?.current_plan?.billing_cycle ||
        subscription?.billing_cycle ||
        subscription?.plan?.billing_cycle ||
        currentProduct.billing_cycle ||
        currentProduct.cycle ||
        "monthly"
    );

    const selectedIndex = (products || []).findIndex((p, idx) => {
      const id = p.id ?? idx;
      return id === selectedProductId;
    });
    const selectedRawId =
      selectedProduct.id ?? (selectedIndex < 0 ? 0 : selectedIndex);
    // Si el producto seleccionado es el actual, por defecto usamos el mismo
    // billing real de la suscripción (currentOriginalBilling) para que al
    // cargar la página no se considere como cambio.
    let selectedDefaultBilling;
    if (hasActiveSubscription && currentProduct && selectedProduct === currentProduct) {
      selectedDefaultBilling = currentOriginalBilling;
    } else {
      selectedDefaultBilling = normalizeBilling(
        selectedProduct.billing_cycle || selectedProduct.cycle || "monthly"
      );
    }
    const selectedBilling = normalizeBilling(
      billingByProduct[selectedRawId] ?? selectedDefaultBilling
    );

    const samePlan = selectedRawId === currentId;
    const sameBilling = selectedBilling === currentOriginalBilling;

    return !(samePlan && sameBilling);
  })();

  const subscriptionCancellationLabel = useMemo(() => {
    if (!subscription) return null;

    // Preferimos los campos del propio objeto subscription, porque en el backend
    // ya vienen normalizados (por ejemplo cancel_at_period_end, current_period_end).
    const normalizeBool = (val) =>
      val === true || val === 1 || val === "1" || val === "true";

    const cancelAtPeriodEnd =
      normalizeBool(subscription.cancel_at_period_end) ||
      normalizeBool(subscription.stripe_subscription?.cancel_at_period_end);

    const cancelTs =
      subscription.canceled_at ||
      subscription.cancel_at ||
      subscription.stripe_subscription?.canceled_at ||
      subscription.stripe_subscription?.cancel_at ||
      null;

    const periodEndTs =
      subscription.current_period_end ||
      subscription.current_period_end_date ||
      subscription.stripe_subscription?.current_period_end ||
      null;

    // Para el mensaje genérico (cuando ya está cancelada del todo) usamos la
    // mejor fecha disponible, priorizando canceled_at y luego current_period_end.
    const rawTs = cancelTs || periodEndTs;

    if (!rawTs) return null;

    let dateObj;
    if (typeof rawTs === "number") {
      // Stripe suele enviar timestamps en segundos
      const ms = rawTs > 1e12 ? rawTs : rawTs * 1000;
      dateObj = new Date(ms);
    } else {
      dateObj = new Date(rawTs);
    }
    if (Number.isNaN(dateObj.getTime())) return null;

    const formatted = formatDate(dateObj);

    const isCancelledStatus = isSubscriptionCancelled(subscription);

    if (isCancelledStatus) {
      // Si ya está cancelada definitivamente, mostramos solo la fecha de cancelación
      // (o la mejor fecha disponible si no hay canceled_at).
      return t("page.status.cancelledOn", { date: formatted });
    }

    if (cancelAtPeriodEnd && (cancelTs || periodEndTs)) {
      // Caso: cancelada para el final del período. Mostramos ambas fechas si es posible.
      let cancelDateStr = formatted;
      let endDateStr = formatted;

      if (cancelTs) {
        let d;
        if (typeof cancelTs === "number") {
          const ms = cancelTs > 1e12 ? cancelTs : cancelTs * 1000;
          d = new Date(ms);
        } else {
          d = new Date(cancelTs);
        }
        if (!Number.isNaN(d.getTime())) {
          cancelDateStr = formatDate(d);
        }
      }

      if (periodEndTs) {
        let d;
        if (typeof periodEndTs === "number") {
          const ms = periodEndTs > 1e12 ? periodEndTs : periodEndTs * 1000;
          d = new Date(ms);
        } else {
          d = new Date(periodEndTs);
        }
        if (!Number.isNaN(d.getTime())) {
          endDateStr = formatDate(d);
        }
      }

      return t("page.status.willCancelOnWithEnd", {
        cancelDate: cancelDateStr,
        endDate: endDateStr,
      });
    }

    return null;
  }, [subscription, formatDate, t]);

  const isSubscriptionCancellationLocked = useMemo(() => {
    if (!subscription) return false;

    const normalizeBool = (val) =>
      val === true || val === 1 || val === "1" || val === "true";

    const cancelAtPeriodEnd =
      normalizeBool(subscription.cancel_at_period_end) ||
      normalizeBool(subscription.stripe_subscription?.cancel_at_period_end);

    return cancelAtPeriodEnd || isSubscriptionCancelled(subscription);
  }, [subscription]);

  const refreshData = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
      let subData = null;

      // Cargamos la suscripción actual, pero si el backend devuelve 404
      // (no hay suscripción para la organización) lo tratamos como "sin suscripción"
      try {
        subData = await getOrganizationSubscription(organizationId);
      } catch (e) {
        const status = e?.response?.status;
        if (status && status !== 404) {
          console.error("Error fetching organization subscription", e?.response || e);
        } else {
          console.warn("No active subscription for organization", e?.response || e);
        }
      }

      if (isSubscriptionCancelled(subData)) {
        subData = null;
      }

      setSubscription(subData || null);

      // Si el backend ya refleja el price actual, limpiamos el override.
      // Esto permite que el frontend vuelva a confiar en la API cuando el backend se sincroniza.
      if (subData && activePriceOverride) {
        const backendActive = getSubscriptionActivePriceId(subData);
        if (backendActive && backendActive === activePriceOverride) {
          setActivePriceOverride(null);
        }
      }

      // Siempre intentamos cargar los productos disponibles, independientemente
      // de si hay o no una suscripción activa
      const productsData = await getSubscriptionProductsDropdown();

      const hasSub = !!subData;

      let normalized = normalizeSubscriptionProducts(productsData || []);

      // Si NO hay suscripción activa, ordenamos los productos de menor a mayor
      // tomando como referencia el precio mensual
      if (!hasSub) {
        normalized = [...normalized].sort((a, b) => {
          const amountA = getAmountWithBilling(a, "monthly");
          const amountB = getAmountWithBilling(b, "monthly");
          return amountA - amountB;
        });
      }

      setProducts(normalized);

      // Determinamos qué producto debe aparecer como suscripción actual
      let defaultId = null;
      if (hasSub && normalized.length > 0) {
        const activePriceId =
          activePriceOverride || getSubscriptionActivePriceId(subData);
        if (activePriceId) {
          const byPrice = normalized.find((p) => productHasPriceId(p, activePriceId));
          if (byPrice) {
            const idx = normalized.indexOf(byPrice);
            defaultId = byPrice.id ?? (idx < 0 ? 0 : idx);
          }
        }

        const subName = subData?.subscription_name || subData?.name;
        const subProductId =
          subData?.stripe_product_id ||
          subData?.product_stripe_id ||
          subData?.stripe_product ||
          subData?.product_id ||
          subData?.product?.stripe_product_id ||
          null;

        let match = null;
        if (defaultId === null) {
          match = normalized.find((p) => {
            const pid =
              p.stripe_product_id ||
              p.product_stripe_id ||
              p.stripe_product ||
              p.product_id ||
              null;
            return subProductId && pid && pid === subProductId;
          });
        }

        if (defaultId === null && !match && subName) {
          match = normalized.find((p) => {
            const name = p.subscription_name || p.name;
            return name === subName;
          });
        }

        if (defaultId === null && match) {
          const idx = normalized.indexOf(match);
          defaultId = match.id ?? (idx < 0 ? 0 : idx);
        }
      }

      // Fallback: si no encontramos match, usamos el primer producto
      if (defaultId === null) {
        const first = normalized[0];
        defaultId = first ? first.id ?? 0 : null;
      }

      setSelectedProductId(defaultId);
    } catch (e) {
      console.error("Error fetching subscription products", e?.response || e);
      setProducts([]);
      setSelectedProductId(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, activePriceOverride]);

  const handleReviewChanges = () => {
    if (!selectedProduct) return;
    setCheckoutError(null);
    setCheckoutCompleted(false);
    setShowReviewModal(true);
  };

  const handleConfirmCheckout = async () => {
    if (!selectedProduct) return;
    setCheckoutLoading(true);
    setCheckoutCompleted(false);
    let shouldCloseModal = true;
    try {
      const index = (products || []).findIndex((p, idx) => {
        const id = p.id ?? idx;
        return id === selectedProductId;
      });
      const fallbackIndex = index < 0 ? 0 : index;
      const rawId = selectedProduct.id ?? fallbackIndex;
      const defaultBilling =
        selectedProduct.billing_cycle || selectedProduct.cycle || "monthly";
      const billing = billingByProduct[rawId] ?? defaultBilling;
      const targetPrice = getPriceForBilling(selectedProduct, billing);
      const toPriceId =
        targetPrice?.stripe_id || targetPrice?.id || targetPrice?.price_stripe_id || null;
      if (!toPriceId) {
        console.error("Missing toPriceId for plan change/checkout", {
          targetPrice,
          selectedProduct,
        });
        return;
      }

      try {
        const data = await createSubscriptionCheckout({
          planStripeId: toPriceId,
          billingCycle: billing,
        });
        const url = data?.url || data?.checkout_url || data?.checkoutUrl;
        if (url) {
          window.location.href = url;
        } else {
          console.error("Checkout response without redirect url", data);
          setCheckoutError(t("page.errorCheckout"));
          shouldCloseModal = false;
        }
      } catch (e) {
        console.error("Checkout create failed (suscriptions)", {
          planStripeId: toPriceId,
          billingCycle: billing,
          response: e?.response,
          error: e,
        });
        setCheckoutError(e?.response?.data?.error || t("page.errorCheckout"));
        shouldCloseModal = false;
      }
    } catch (e) {
      console.error("Error processing subscription action", e?.response || e);
      setCheckoutError(e?.response?.data?.error || t("page.errorCheckout"));
      shouldCloseModal = false;
    } finally {
      setCheckoutLoading(false);
      if (shouldCloseModal) setShowReviewModal(false);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    refreshData();
  }, [organizationId, refreshData]);


  const handleCancelSubscription = async (e) => {
    e?.preventDefault?.();
    if (!organizationId || !subscription) return;
    setLoading(true);
    try {
      await cancelOrganizationSubscription({
        organizationId,
        cancellationReason:
          cancelReason ||
          t("page.defaultCancelReason"),
      });

      // Después de cancelar, volvemos a consultar la suscripción real
      // para obtener el objeto completo (incluyendo product/plan) o
      // detectar si ya no hay suscripción activa.
      try {
        const updated = await getOrganizationSubscription(organizationId);
        const normalized = isSubscriptionCancelled(updated) ? null : updated;
        setSubscription(normalized || null);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setSubscription(null);
        } else {
          console.error("Error refetching subscription after cancel", err?.response || err);
        }
      }
    } catch (e) {
      console.error("Error cancelling subscription", e?.response || e);
    } finally {
      setLoading(false);
    }
  };

  const subscriptionProgress = (() => {
    if (!subscription) return null;

    const start = subscription.current_period_start || subscription.start_date;
    const end = subscription.current_period_end;
    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);
    const now = new Date();

    const totalMs = endDate.getTime() - startDate.getTime();
    if (totalMs <= 0) return null;

    const elapsedMs = Math.min(Math.max(now.getTime() - startDate.getTime(), 0), totalMs);
    const percent = Math.round((elapsedMs / totalMs) * 100);

    const formatDMY = (d) => {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return {
      startDate: formatDMY(startDate),
      endDate: formatDMY(endDate),
      percent,
    };
  })();

  const creditPoolSummary = (() => {
    if (!subscription) {
      return {
        state: "no_subscription",
        purchased: null,
        consumed: null,
        remaining: null,
        percent: 0,
        next: null,
      };
    }

    const end = subscription?.current_period_end;
    let next = null;
    if (end) {
      const endDate = new Date(end);
      if (!Number.isNaN(endDate.getTime())) {
        const now = new Date();
        const days = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / 86400000));

        // Mostrar la fecha de próximo refresh en formato dd/MM/yyyy, por ejemplo 18/01/2026
        const day = String(endDate.getDate()).padStart(2, "0");
        const month = String(endDate.getMonth() + 1).padStart(2, "0");
        const year = endDate.getFullYear();
        const date = `${day}/${month}/${year}`;

        next = { days, date };
      }
    }

    const pool = subscription?.credit_pool;
    if (!pool) {
      return {
        state: "no_credits",
        purchased: null,
        consumed: null,
        remaining: null,
        percent: 0,
        next,
      };
    }

    const purchased = Number(pool.total_purchased ?? 0) || 0;
    const consumed = Number(pool.total_consumed ?? 0) || 0;
    const remaining = Number(pool.remaining_credits ?? 0) || 0;

    const denom = Math.max(purchased, consumed, purchased + remaining, 0);
    const percent = denom > 0 ? Math.min(Math.round((consumed / denom) * 100), 100) : 0;

    return {
      state: "ready",
      purchased,
      consumed,
      remaining,
      percent,
      next,
    };
  })();

  // Barra de consumo segn el plan actual (por ejemplo, 500 cr e9ditos incluidos en el plan)
  const planUsageSummary = useMemo(() => {
    // Siempre devolvemos un objeto, incluso si no hay suscripcin o l5mite,
    // para poder mostrar una barra vaca similar a la de "Purchased credits".
    if (!subscription) {
      return {
        limit: 0,
        consumed: 0,
        percent: 0,
      };
    }

    // 1) Preferimos usar los campos de uso mensual que devuelve el backend
    // dentro de subscription.monthly_usage (monthly_usage_limit, total_consumed, remaining_credits).
    const monthlyUsage = subscription?.monthly_usage || null;

    const rawLimitFromSubscription =
      subscription?.monthly_usage_limit ??
      monthlyUsage?.monthly_usage_limit ??
      monthlyUsage?.monthly_usage_limit ??
      monthlyUsage?.limit ??
      null;

    let rawConsumedFromSubscription =
      monthlyUsage?.total_consumed ??
      monthlyUsage?.consumed ??
      null;

    const rawRemainingFromSubscription =
      monthlyUsage?.remaining_credits ??
      monthlyUsage?.remaining ??
      null;

    const limitFromSubscription = Number(rawLimitFromSubscription ?? 0) || 0;

    // Si no viene total_consumed pero sí tenemos límite y remaining_credits,
    // calculamos consumo como (limit - remaining).
    if (
      (rawConsumedFromSubscription === null || rawConsumedFromSubscription === undefined) &&
      limitFromSubscription > 0 &&
      rawRemainingFromSubscription !== null &&
      rawRemainingFromSubscription !== undefined
    ) {
      rawConsumedFromSubscription =
        Number(limitFromSubscription) - Number(rawRemainingFromSubscription);
    }

    const consumedFromSubscription = Number(rawConsumedFromSubscription ?? 0) || 0;

    if (limitFromSubscription > 0) {
      const safeConsumed = Math.min(consumedFromSubscription, limitFromSubscription);
      const percent = Math.min(
        Math.round((safeConsumed / limitFromSubscription) * 100),
        100
      );

      return {
        limit: limitFromSubscription,
        consumed: safeConsumed,
        percent,
      };
    }

    // 2) Fallback: usamos la información del producto/plan actual y el credit_pool
    // como en la lógica original, por compatibilidad con APIs antiguas.
    if (!currentProduct) {
      return {
        limit: 0,
        consumed: 0,
        percent: 0,
      };
    }

    const pool = subscription.credit_pool;
    if (!pool) {
      return {
        limit: 0,
        consumed: 0,
        percent: 0,
      };
    }

    const plansValue =
      subscription?.plans || subscription?.subscription?.plans || null;
    let activePlan = null;
    if (Array.isArray(plansValue)) {
      activePlan =
        plansValue.find((p) => p?.active === true) || plansValue[0] || null;
    } else if (plansValue && typeof plansValue === "object") {
      activePlan = plansValue;
    }

    const normalizeBilling = (value) =>
      value === "year" || value === "yearly" ? "yearly" : "monthly";

    const billing = normalizeBilling(
      activePlan?.billing_cycle ||
        subscription?.current_plan?.billing_cycle ||
        subscription?.billing_cycle ||
        subscription?.plan?.billing_cycle ||
        currentProduct.billing_cycle ||
        currentProduct.cycle ||
        "monthly"
    );

    const priceForBilling = currentProduct?.prices?.[billing] || null;

    const rawLimit =
      priceForBilling?.monthly_usage_limit ??
      priceForBilling?.usage_limit ??
      currentProduct?.monthly_usage_limit ??
      currentProduct?.usage_limit ??
      null;

    const limit = Number(rawLimit ?? 0) || 0;
    if (limit <= 0) {
      return {
        limit: 0,
        consumed: 0,
        percent: 0,
      };
    }

    const consumed = Number(pool.total_consumed ?? 0) || 0;
    const percent = Math.min(Math.round((consumed / limit) * 100), 100);

    return {
      limit,
      consumed,
      percent,
    };
  }, [subscription, currentProduct]);

  return (
    <div className="suscriptions-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="suscriptions-title">{t("page.title")}</h1>
          <p className="suscriptions-subtitle">{t("page.subtitle")}</p>
        </div>
        <div className="flex gap-4">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => navigate("/app/suscriptions/payment-history")}
          >
            {t("page.paymentHistory.goTo")}
          </Button>
        </div>
      </div>

      {loading && <p>{t("page.loading")}</p>}

      {subscriptionProgress && (
        <div className="suscriptions-period-summary">
          <div className="suscriptions-period-header">
            <span className="suscriptions-period-title">{t("page.period.title")}</span>
            {creditPoolSummary.next && (
              <span className="suscriptions-credit-next">
                {t("page.creditPool.nextRefresh", {
                  days: creditPoolSummary.next.days,
                  date: creditPoolSummary.next.date,
                })}
              </span>
            )}
          </div>
          <div className="suscriptions-period-dates">
            <span>
              {t("page.period.start")}: {subscriptionProgress.startDate}
            </span>
          </div>
          <div className="suscriptions-progress-bar">
            <div
              className="suscriptions-progress-bar-fill"
              style={{ width: `${subscriptionProgress.percent}%` }}
            />
          </div>
          <div className="suscriptions-progress-label">
            {subscriptionProgress.percent}% {t("page.period.elapsed")}
          </div>
        </div>
      )}

      {!loading && (
        <div className="suscriptions-credit-summary">
          <div className="suscriptions-credit-header">
            <span className="suscriptions-credit-title">
              {t("page.creditPool.title")}
            </span>
          </div>

          <div className="suscriptions-usage-section suscriptions-plan-usage">
            <div className="suscriptions-plan-usage-header">
              <span className="suscriptions-plan-usage-title">
                {t("page.planUsage.title")}
              </span>
            </div>
            {(() => {
              const limit = Number(planUsageSummary.limit ?? 0) || 0;
              const consumed = Number(planUsageSummary.consumed ?? 0) || 0;
              const remaining = Math.max(0, limit - consumed);

              const fmtNice = (v) => {
                const s = v.toFixed(2);
                if (s.endsWith(".00")) return s.slice(0, -3);
                return s.replace(/(\.\d)0$/, "$1");
              };

              return (
                <div className="suscriptions-plan-usage-meta-row">
                  <span className="suscriptions-plan-usage-meta">
                    {fmtNice(consumed)} / {fmtNice(limit)} {t("page.planUsage.usedLabel")}
                  </span>
                  <span className="suscriptions-plan-usage-meta suscriptions-plan-usage-right">
                    {fmtNice(remaining)} {t("page.planUsage.leftLabel")}
                  </span>
                </div>
              );
            })()}
            <div className="suscriptions-progress-bar">
              <div
                className="suscriptions-progress-bar-fill"
                style={{ width: `${planUsageSummary.percent ?? 0}%` }}
              />
            </div>
            <div className="suscriptions-progress-label">
              {Math.round(planUsageSummary.percent ?? 0)}% {t("page.planUsage.ofLimit")}
            </div>
          </div>

          <div className="suscriptions-usage-section suscriptions-pool-usage">
            <div className="suscriptions-plan-usage-header">
              <span className="suscriptions-plan-usage-title">
                {t("page.poolUsage.title")}
              </span>
            </div>
            {(() => {
              const purchased = Number(creditPoolSummary.purchased ?? 0) || 0;
              const consumed = Number(creditPoolSummary.consumed ?? 0) || 0;
              const remaining = Math.max(0, purchased - consumed);

              const fmtNice = (v) => {
                const s = v.toFixed(2);
                if (s.endsWith(".00")) return s.slice(0, -3);
                return s.replace(/(\.\d)0$/, "$1");
              };

              return (
                <div className="suscriptions-plan-usage-meta-row">
                  <span className="suscriptions-plan-usage-meta">
                    {fmtNice(consumed)} / {fmtNice(purchased)} {t("page.planUsage.usedLabel")}
                  </span>
                  <span className="suscriptions-plan-usage-meta suscriptions-plan-usage-right">
                    {fmtNice(remaining)} {t("page.planUsage.leftLabel")}
                  </span>
                </div>
              );
            })()}
            <div className="suscriptions-progress-bar">
              <div
                className="suscriptions-progress-bar-fill"
                style={{ width: `${creditPoolSummary.percent}%` }}
              />
            </div>
            <div className="suscriptions-progress-label">
              {Math.round(creditPoolSummary.percent ?? 0)}% {t("page.poolUsage.ofLimit")}
            </div>
          </div>

          {/* {creditPoolSummary.state !== "ready" && (
            <div className="suscriptions-credit-empty">
              {creditPoolSummary.state === "no_subscription"
                ? t("page.creditPool.noSubscription")
                : t("page.creditPool.noCredits")}
            </div>
          )} */}

          <div className="suscriptions-credit-footer">
            <div 
              className="link-details"
              onClick={() => navigate("/app/suscriptions/credit-usage")}
            >
              {t("page.creditPurchases.viewDetails")}
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={loading || !organizationId || !subscription}
              className="suscriptions-credit-add-btn"
              onClick={() => setShowCreditPackagesModal(true)}
            >
              {t("page.creditPackages.openButton")}
            </Button>
          </div>
        </div>
      )}

      <SubscriptionPlansSection
        t={t}
        hasActiveSubscription={hasActiveSubscription}
        currentProduct={currentProduct}
        products={products}
        subscription={subscription}
        selectedProductId={selectedProductId}
        billingByProduct={billingByProduct}
        onChangeBilling={handleChangeBilling}
        onSelectProduct={handleSelectProduct}
        getAmountWithBilling={getAmountWithBilling}
        currentTotal={currentTotal}
        newTotal={newTotal}
        onReviewChanges={handleReviewChanges}
        loading={loading || checkoutLoading}
        selectedProduct={selectedProduct}
        hasChanges={hasChanges}
        cancellationLabel={subscriptionCancellationLabel}
        cancellationLocked={isSubscriptionCancellationLocked}
      />

      <ReviewChangesModal
        open={showReviewModal}
        t={t}
        mode={hasActiveSubscription ? "change" : "create"}
        currentTotal={currentTotal}
        newTotal={newTotal}
        error={checkoutError}
        loading={checkoutLoading}
        completed={checkoutCompleted}
        onClose={() => setShowReviewModal(false)}
        onConfirm={handleConfirmCheckout}
      />

      <CreditPackagesModal
        open={showCreditPackagesModal}
        t={t}
        onClose={() => setShowCreditPackagesModal(false)}
      />

      <section className="suscriptions-section">
        <CancelSubscriptionModal
          open={showCancelConfirm}
          t={t}
          loading={loading}
          disabled={!organizationId || !subscription || isSubscriptionCancellationLocked}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={async () => {
            await handleCancelSubscription();
            setShowCancelConfirm(false);
          }}
        />

        <div className="cancel-subscription">
          <ButtonDelete
            type="button"
            variant="delete"
            size="sm"
            disabled={
              loading ||
              !organizationId ||
              !subscription ||
              isSubscriptionCancellationLocked
            }
            onClick={() => setShowCancelConfirm(true)}
          >
            {t("page.cancel")}
          </ButtonDelete>
        </div>
      </section>
    </div>
  );
}

export default Suscriptions;
